import React from 'react';
import { Link } from 'react-router-dom';
import { Coins, Check, ArrowRight, Shield, Code, FileText, TrendingUp, Lock, Globe } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const CryptoServicesPage = () => {
  const { services } = mockData;
  const cryptoServices = services.crypto;

  const blockchainNetworks = [
    { name: 'Ethereum (ERC20)', logo: '/logos/ethereum.png', color: 'from-blue-50 to-purple-50' },
    { name: 'Binance Smart Chain (BEP20)', logo: '/logos/bsc.png', color: 'from-yellow-50 to-orange-50' },
    { name: 'Polygon (MATIC)', logo: '/logos/polygon.png', color: 'from-purple-50 to-pink-50' },
    { name: 'Avalanche (AVAX)', logo: '/logos/avalanche.png', color: 'from-red-50 to-pink-50' },
    { name: 'Solana (SOL)', logo: '/logos/solana.png', color: 'from-cyan-50 to-purple-50' }
  ];

  const securityFeatures = [
    { icon: Shield, title: 'Güvenlik Auditi', description: 'Kapsamlı güvenlik testleri' },
    { icon: Lock, title: 'Akıllı Sözleşme', description: 'Şeffaf ve güvenilir kontratlar' },
    { icon: Code, title: 'Açık Kaynak', description: 'Doğrulanabilir kod yapısı' },
    { icon: TrendingUp, title: 'Gas Optimizasyonu', description: 'Düşük işlem ücretleri' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title="Kripto Token Geliştirme & Blockchain"
        description="Viral Strateji ile kendi kripto token projenizi hayata geçirin. Ethereum, BSC, Polygon ve Solana ağlarında güvenli akıllı sözleşme çözümleri."
        keywords="kripto token geliştirme, akıllı sözleşme yazma, blockchain çözümleri, erc20 token, bep20 token, solana token"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-slate-800 dark:via-purple-950 dark:to-slate-900 transition-colors duration-300">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {cryptoServices.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {cryptoServices.subtitle}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2.5 rounded-full font-semibold text-base hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>Token Projesi Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Supported Networks */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Desteklenen Blockchain Ağları
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Projenize en uygun blockchain ağında token oluşturun.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {blockchainNetworks.map((network, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 text-center flex flex-col items-center group">
                <div className={`bg-gradient-to-br ${network.color} w-20 h-20 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 shadow-sm`}>
                  <img src={network.logo} alt={network.name} className="w-12 h-12 object-contain" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">{network.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {cryptoServices.items.map((service, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-600" />
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0 mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    <span className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 px-4 py-2 rounded-xl font-semibold text-sm self-start sm:self-auto">
                      {service.price}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/iletisim"
                    className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 px-6 rounded-full font-semibold text-sm text-center hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform group-hover:scale-105"
                  >
                    Teklif Al
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Güvenlik Önceliğimizdir
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tüm token projelerinde en yüksek güvenlik standartlarını uyguluyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Token Geliştirme Süreci
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Güvenli ve profesyonel token geliştirme sürecimiz.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                { step: 1, title: 'Konsept & Planlama', duration: '1-2 Gün', description: 'Token ekonomisi ve teknik detayları planlıyoruz.' },
                { step: 2, title: 'Smart Contract Geliştirme', duration: '3-5 Gün', description: 'Güvenli akıllı sözleşme kodları yazıyoruz.' },
                { step: 3, title: 'Testnet Deployment', duration: '2-3 Gün', description: 'Test ağında kapsamlı testler gerçekleştiriyoruz.' },
                { step: 4, title: 'Güvenlik Auditi', duration: '2-3 Gün', description: 'Detaylı güvenlik analizi ve optimizasyon.' },
                { step: 5, title: 'Mainnet Launch', duration: '1 Gün', description: 'Ana ağda token lansmanı ve doğrulama.' }
              ].map((phase, index) => (
                <div key={index} className="flex items-center space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl">
                    {phase.step}
                  </div>
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{phase.title}</h3>
                        <p className="text-gray-600 mt-1">{phase.description}</p>
                      </div>
                      <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                        {phase.duration}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
            Token Projenizi Başlatmaya Hazır mısınız?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
            Blockchain uzmanlarımızla görüşerek token projenizi güvenle hayata geçirin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-white text-purple-600 px-6 py-2.5 rounded-full font-semibold text-base hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <span>Token Projesi Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/portfolyo"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-6 py-2.5 rounded-full font-semibold text-base hover:bg-white hover:text-purple-600 transition-all duration-300 transform hover:scale-105"
            >
              <span>Örnek Projeler</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CryptoServicesPage;